import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, signal, viewChild, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router, RouterLink } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { groupBy } from 'lodash';
import { combineLatest, EMPTY, from, lastValueFrom, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, take, tap, toArray } from 'rxjs/operators';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { ITab } from 'src/app/model/tab';
import { IWizardQueryParams } from 'src/app/model/thingie-creation-wizard/wizard-query-parameters';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ICard, StackCardDialogInterface } from 'src/app/model/thingie/thingie-card';
import { AuthService } from 'src/app/services/auth/auth.service';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { ThingieImportExportService } from 'src/app/services/data/thingie-import-export.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { PlotlyGeneratorProviderService } from 'src/app/services/plotly-generator/plotly-generator-provider.service';
import { TabStorageService } from 'src/app/services/tab-storage/tab-storage.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { UserService } from 'src/app/services/user/user.service';
import {
  DASHBOARD_PAGE_THINGIES_PROJECT_STORAGE_KEY,
  getDashboardSetting,
  IThingieSortingAndFilter,
  updateDashboardSetting
} from 'src/app/utility/dashboard-helper';
import { createDefaultTab, createDefaultThingieListFilter } from 'src/app/utility/default-object-creator';
import { extractDefaultGraphConfigsFromThingie } from 'src/app/utility/default-tab-helper';
import { translateDeviceTypeToGroupName as getDeviceGroupNameForDeviceType } from 'src/app/utility/device-types';
import { PureFunctionCallPipe } from 'src/app/utility/pipes/pure-member-call/pure-member-call.pipe';
import { SbiResizeObserver } from 'src/app/utility/resize-observer';
import { TabLoader } from 'src/app/utility/tab-loader';
import { ThingieHelper } from 'src/app/utility/thingie-helper';
import { ThingieStatusHelper } from 'src/app/utility/thingie-status-helper';
import { cardsFromThingieGroup, getTabForCard, initializeCards } from 'src/app/utility/thingie/thingie-card-generator';
import { isNotUndefined } from 'src/app/utility/types';
import { DefaultConfig } from 'src/assets/default.config';
import { DefaultUrls } from 'src/assets/default.urls';
import { SubSink } from 'subsink';
import { BoldPipeByMatchedPart } from '../../../utility/pipes/bold-pipes';
import { CocoSortHeaderComponent } from '../../coco-sort-header/coco-sort-header.component';
import { AnnotationHandlingDialogComponent, AnnotationHandlingDialogMode } from '../../dialogs/annotation/annotation-handling-dialog/annotation-handling-dialog.component';
import { DialogSizeMode } from '../../dialogs/dialog/dialog-size-mode.enum';
import { DialogService, OpenDialogRef } from '../../dialogs/dialog/dialog.service';
import { AssignedDevicesDialogComponent } from '../../dialogs/experiment-list/assigned-devices-dialog/assigned-devices-dialog.component';
import { IObjectExportDialogArgs as ExportDialogArgs } from '../../dialogs/object-export-dialog/object-export-dialog.component';
import { FilterByProjectComponent } from '../../filter-by-project/filter-by-project.component';
import { PaginationFilterPageSizeComponent } from '../../pagination/pagination-filter-page-size/pagination-filter-page-size.component';
import { StringEllipsisComponent } from '../../string-ellipsis/string-ellipsis.component';
import { TaskStatusBarComponent } from '../../task-status-bar/task-status-bar.component';
import { ThingieStateChipComponent } from '../../thingie-state-chip/thingie-state-chip.component';
import { DisplayThingie, IGroupedThingies, IThingieListAction } from '../list-thingies.component';
import { StackCardDialogComponent } from '../stack-card-dialog/stack-card-dialog.component';


interface ISearchTerm {
  searchString: string;
  searchField: SearchFieldEnum;
}


enum SearchFieldEnum {
  thingieName,
  project,
  process,
  devices,
}


type ExtraThingieInfo_Device = {
  type: 'device';
  device: DisplayThingie['assignedDevices'][number];
};


type ExtraThingieInfo_Overflow = {
  type: 'overflowingDevices';
  devices: DisplayThingie['assignedDevices'][number][];

  /**
   * Whether the overflowing devices include a device with a matching part
   * in it's name.
   *
   * If true, the overflow indicator shall be highlighted accordingly.
   */
  hasDeviceWithMatchingPart: boolean;
};


/**
 * Extra information for each thingie in the group.
 *
 * Accessed using the array-index of the display thingie in the group.
 * E.g. group.thingies[0] => group.extraThingieInfo[0]
 *
 * This is used to display additional information in the table,
 * such as the assigned devices and whether there are more devices which can't be shown
 * due to space constraints.
 *
 * This is a list.
 * Each entry in the list represents either a device to be displayed or
 * the overflow indicator, which displays the number of devices that can't be
 * displayed.
 *
 * The overflow indicator shall only appear once per ExtraThingieInfo array &
 * shall appear last in the list.
 */
type ExtraThingieInfo = (ExtraThingieInfo_Device | ExtraThingieInfo_Overflow)[];


/**
 * Extension of IGroupedThingies with additional properties.
 */
interface IGroupedThingiesWithExtra extends IGroupedThingies {

  /**
   * All devices assigned to the thingies in this group.
   *
   * Used to show a sorted list of devices.
   */
  devicesInGroup: { id: string; name: string }[];

  /**
   * String passed to TitleWithChipsComponent to display a combination of
   * abbreviations of all device types.
   *
   * E.g. "[LIS+CGQ]"
   */
  deviceBadgeString: string;

  /**
   * Extra information for each thingie in the group.
   *
   * Accessed using the array-index of the display thingie in the group.
   *
   * E.g. group.thingies[0] => group.extraThingieInfo[0]
   */
  extraThingieInfo: ExtraThingieInfo[];
}

interface INamed {
  name: string;
}

function getAssignedDevicesByMatchingTermSorter(term: string) {
  return (a: INamed, b: INamed) => {
    const filterValue = term.toLowerCase();
    const aMatches = a.name.toLowerCase().includes(filterValue);
    const bMatches = b.name.toLowerCase().includes(filterValue);

    if (aMatches && !bMatches) {
      return -1;
    } else if (!aMatches && bMatches) {
      return 1;
    } else {
      return a.name.localeCompare(b.name);
    }
  };
}


@Component({
  selector: 'app-list-thingies-filter',
  templateUrl: './list-thingies-filter.component.html',
  styleUrls: ['./list-thingies-filter.component.scss'],
  imports: [
    CommonModule,
    FilterByProjectComponent,
    PaginationFilterPageSizeComponent,
    TaskStatusBarComponent,
    MaterialImportsModule,
    IonIcon,
    CocoSortHeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    StringEllipsisComponent,
    ThingieStateChipComponent,
    DatePipe,
    BoldPipeByMatchedPart,
    PureFunctionCallPipe
  ]
})
export class ListThingiesFilterComponent implements AfterViewInit, OnDestroy {
  protected readonly urls = DefaultUrls;

  @Input() public loadingData = false;
  @Input() public groupByExperimentChecked = false;
  private readonly defaultDebounce_Milliseconds = DefaultConfig.general.userInputDefaultDebounce_Milliseconds;

  @ViewChild(MatTable)
  private table?: MatTable<IGroupedThingies>;

  @ViewChild(MatSort) private sort!: MatSort;

  @Input()
  public archive = false;
  protected archivableSelections = true;

  @Input()
  public pageSize = 10;

  @Output() public callbackFunction: EventEmitter<number> = new EventEmitter();
  @Output() public filterCallbackFunction: EventEmitter<IThingieListAction> = new EventEmitter();

  protected selectedThingies = signal<IThingie[]>([]);
  protected forceToShowCalculationSpinner = signal<boolean>(false);
  protected onlyOneExperimentIsSelected  = false;
  private dialogData?: StackCardDialogInterface;

  private _isExpandedMap: Map<string, boolean> = new Map();
  protected isExpanded = (i: number, rowData: IGroupedThingies) => rowData.showGroupDetailToggle;

  private dialogRef?: OpenDialogRef<string | undefined>;

  protected showGroupDetails(group: IGroupedThingies, show: boolean) {
    group.showGroupDetailToggle = show;
    this._isExpandedMap.set(group.thingies[0].thingieId, show);

    this.updateCombinedCollapseState();

    // refresh table
    this.table?.renderRows();
  }

  private get thingieList(): IThingie[] {
    return this._thingieGroups.flatMap(group => group.thingies.map(displayThingie => displayThingie.thingie));
  }

  @Input()
  public set thingieGroups(newList: IGroupedThingies[] | null) {
    if (newList === null || newList.length === 0) {
      this._thingieGroups = [];
    } else {
      this.applyNewGroupsCheckedStates(newList);

      this._thingieGroups = this.applyFilteringAndSortingToNewGroups(newList);

      this._thingieGroups.forEach(group => {
        group.thingies.forEach(thingie => {
          thingie.thingieStatus = ThingieStatusHelper.getThingieStatus(thingie.thingie).status;
        });
      });

      this.tableDataSource.data = this._thingieGroups;

      this.updateTasks$.next();
    }
  }

  private updateTasks$ = new Subject<void>();

  private _thingieGroups: IGroupedThingiesWithExtra[] = [];

  // prepares list of selected thingie ids
  protected tableDataSource = new MatTableDataSource<IGroupedThingies>([]);
  private subscriptions = new SubSink();

  // form controls for filtering
  protected thingieNameFilter = new FormControl('');
  protected deviceFilter = new FormControl('');
  private _taskFilter = new FormControl('');

  private formattedDateFilter: string | null = null;
  private dateFilter: Date | null = null;

  protected projectFilter = 'all';
  private currentUserID!: string;
  protected isEditor = signal<boolean>(false);
  private editorCheckTrigger$ = new Subject<IThingie[]>();
  protected isAdmin = signal<boolean>(false);

  // filter-triggering counter
  // will incremeneted each time to always provide a new filter string
  private filterCounter = 0;

  private searchTerms: ISearchTerm[] = [];

  protected allGroupsCollapseState: 'open' | 'collapsed' | 'indeterminate' = 'collapsed';
  protected archiveActionInProgress = false;

  /**
   * The table's column definitions used for groups which are not expanded
   */
  @Input()
  public displayedColumns = ['status', 'thingieName', 'thingieStatus', 'assignedDevices', 'dateCreated'];


  private readonly assignedDevicesHeader = viewChild('assignedDevicesHeader', { read: ElementRef });

  /**
   * The table's column definitions used for groups which are expanded
   */
  protected displayedDetailColumns = this.displayedColumns.map(str => str + '_detail');

  // Map<Thingie ID, boolean>
  private thingieCheckedStates = new Map<string, boolean>();
  // Map<Group Name, boolean>
  private groupCheckedStates = new Map<string, 'all' | 'some' | 'none'>();

  private readonly boldenMatchingPartPipe = new BoldPipeByMatchedPart();

  private applyFilteringAndSortingToNewGroups(newList: IGroupedThingies[]) {
    const nameSortedGroups = newList.toSorted(function(a, b) {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

    const groupsWithGroupDevices = nameSortedGroups.map<IGroupedThingiesWithExtra>(g => {
      const uniqueDeviceTypes = new Set<string>();

      const devices = g.thingies.flatMap(t => t.assignedDevices)
        .map(d => {
          uniqueDeviceTypes.add(d.type);
          return { id: d.id, name: d.name };
        });

      const innerStr = Array.from(uniqueDeviceTypes)
        .map(t => {
          const group = getDeviceGroupNameForDeviceType(t);
          const first = group.split(' ')[0];

          return first === '' || first === 'Devices' ? 'NoName' : first;
        })
        .join(' + ');

      const extraInfo = this.determineGroupedThingiesExtraInfo(g);

      return {
        ...g,
        devicesInGroup: devices,
        deviceBadgeString: `${innerStr}`,
        extraThingieInfo: extraInfo,
      };
    });

    if (this.deviceFilter.value) {
      const byDeviceFilterSorter = getAssignedDevicesByMatchingTermSorter(this.deviceFilter.value);

      // Move assigned devices that match the filter to the front of the list
      // of assigned devices of thingies
      groupsWithGroupDevices.forEach(group => {
        group.thingies.forEach(thingie => {
          thingie.assignedDevices.sort(byDeviceFilterSorter);
        });

        group.devicesInGroup.sort(byDeviceFilterSorter);
      });
    }
    return groupsWithGroupDevices;
  }

  private determineDeviceOverflowIndicatorWidth() {
    // Create a device overflow indicator element to test its width.
    // For simplicity it will be bold and use the plural form of "devices" and
    // show a double-digit number of devices from the start to reserve enough space.
    //
    // Otherwise we would have to run this method for each thingie in each group and pass
    // the number of assigned devices.
    // => Should save us some layouting time when only running once.

    const testElement = document.createElement('span');
    testElement.style.visibility = 'hidden';
    testElement.style.position = 'absolute';
    testElement.style.left = '-9999px';
    testElement.style.contain = 'layout style paint';
    testElement.innerHTML = `<b>+12 devices</b>`;

    document.body.appendChild(testElement);
    const width = testElement.getBoundingClientRect().width;
    document.body.removeChild(testElement);

    return width;
  }

  private determineColumnWidth(): number {
    const element = this.assignedDevicesHeader()?.nativeElement;

    const box = element.getBoundingClientRect().width ?? -1;

    const styles = window.getComputedStyle(element);
    const border = parseFloat(styles.borderLeftWidth + styles.borderRightWidth);
    const padding = parseFloat(styles.paddingLeft + styles.paddingRight);

    return box - border - padding;
  }

  private determineGroupedThingiesExtraInfo(g: IGroupedThingies): IGroupedThingiesWithExtra['extraThingieInfo'] {
    const deviceOverflowIndicatorWidth = this.determineDeviceOverflowIndicatorWidth();
    const columnWidth = this.determineColumnWidth();
    const deviceFilterValue = this.deviceFilter.value?.toLowerCase() ?? '';

    // Create span to put devices in for measurement
    const testSpan = document.createElement('span');
    testSpan.style.visibility = 'hidden';
    testSpan.style.position = 'absolute';
    testSpan.style.left = '-9999px';
    testSpan.style.contain = 'layout style paint';
    document.body.appendChild(testSpan);

    const clearSpan = () => {
      while (testSpan.firstChild) {
        testSpan.removeChild(testSpan.lastChild!);
      }
    };

    try {
      const extraInfo = g.thingies.map<ExtraThingieInfo>(thingie => {
        let availableWidth = columnWidth;
        let combinedWidth = 0;

        let deviceInfoList: { device: DisplayThingie['assignedDevices'][number]; deviceWidth: number }[] = [];

        // Add device names to the test span
        thingie.assignedDevices.forEach((device, deviceIndex) => {
          clearSpan();

          const tooltip = document.createElement('span');

          const anchor = document.createElement('a');
          anchor.href = '#';

          const nameContainer = document.createElement('span');
          nameContainer.innerHTML = this.boldenMatchingPartPipe.transform(device.name, deviceFilterValue);

          anchor.appendChild(nameContainer);
          tooltip.appendChild(anchor);

          testSpan.appendChild(tooltip);

          if (deviceIndex !== thingie.assignedDevices.length - 1) {
            const separator = document.createElement('span');
            separator.innerHTML = ', ';
            testSpan.appendChild(separator);
          }

          const deviceWidth = testSpan.getBoundingClientRect().width;

          combinedWidth += deviceWidth;

          deviceInfoList.push({
            device,
            deviceWidth,
          });
        }); // device

        let needsIndicator = false;

        if (combinedWidth > availableWidth) {
          // Since the device widths exceed the available space we HAVE to display the indicator
          availableWidth -= deviceOverflowIndicatorWidth;
          needsIndicator = true;
        }


        const devicesToAddToOverflowIndicator: DisplayThingie['assignedDevices'] = [];
        const reversedDeviceInfoList = deviceInfoList.toReversed();
        while (combinedWidth > availableWidth) {
          const deviceInfo = reversedDeviceInfoList.pop();
          if (!deviceInfo) {
            break;
          }

          devicesToAddToOverflowIndicator.push(deviceInfo.device);
          combinedWidth -= deviceInfo.deviceWidth;
        }

        // Add the remaining devices to a list
        const infoList: ExtraThingieInfo = [];
        reversedDeviceInfoList.forEach(device => infoList.push({ type: 'device', device: device.device }));

        if (needsIndicator) {
          // determine if any of the devices in the overflow indicator
          // has a matching part in its name

          let hasDeviceWithMatchingPart = false;
          if (deviceFilterValue) {
            hasDeviceWithMatchingPart = devicesToAddToOverflowIndicator.some(
              d => d.name.toLowerCase().includes(deviceFilterValue.toLowerCase())
            );
          }

          infoList.push({
            type: 'overflowingDevices',
            devices: devicesToAddToOverflowIndicator,
            hasDeviceWithMatchingPart
          });
        }

        return infoList;
      }); // thingie

      return extraInfo;
    } finally {
      document.body.removeChild(testSpan);
    }

  }

  private applyNewGroupsCheckedStates(newList: IGroupedThingies[]) {
    newList.forEach(newGroup => {
      //// Add "new" groups's and thingies's checkbox states.
      //// Otherwise combined states will be wrong
      //// (e.g. the checkbox state for all thingies being calculated on a subset only)
      if (!this.groupCheckedStates.has(newGroup.name)) {
        this.groupCheckedStates.set(newGroup.name, 'none');
      }

      newGroup.thingies.forEach(thingie => {
        if (!this.thingieCheckedStates.has(thingie.thingieId)) {
          this.thingieCheckedStates.set(thingie.thingieId, false);
        }
      });

      //// ---
      newGroup.showGroupDetailToggle = this._isExpandedMap.get(newGroup.thingies[0].thingieId) ?? false;
    });
  }

  protected getThingieCheckedState(thingie: DisplayThingie): boolean {
    return this.thingieCheckedStates.get(thingie.thingieId) ?? false;
  }

  protected getGroupCheckedState(group: IGroupedThingies): 'all' | 'some' | 'none' {
    return this.groupCheckedStates.get(group.name) ?? 'none';
  }

  private defaultPopupMode: 'stacked' | 'single' = 'stacked';

  // to open dialog in full width mode
  private resizeSubscription?: Subscription;

  private dataSortFilter: IThingieSortingAndFilter = createDefaultThingieListFilter();
  private dataSortFilter$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    private thingieService: ThingieService,
    private excelExportService: ThingieImportExportService,
    private tabStorage: TabStorageService,
    private uiNotifications: UINotificationService,
    private log: LogService,
    private auth: AuthService,
    private generator: PlotlyGeneratorProviderService,
    private dataloader: GraphConfigSeriesDataLoaderService,
    private userService: UserService,
    private router: Router,
  ) {
    this.tableDataSource.filterPredicate = this.filterPredicate.bind(this);

    void this.auth.getCurrentUserId().then((id) => {
      this.currentUserID = id;
      const pf = getDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_PROJECT_STORAGE_KEY);
      if (pf !== null) {
        this.projectFilter = pf;
      }
    });

    void this.auth.isAdmin().then((isAdmin) => {
      this.isAdmin.set(isAdmin);
      // if the user is admin, then we can bypass the editor checking
      if (isAdmin) {
        this.isEditor.set(true);
      }
    });
  }


  public ngAfterViewInit() {
    this.subscribeToFilters();
    this.intializeSearchTermArray();
    this.tableDataSource.sort = this.sort;
    this.tableDataSource.sortingDataAccessor = (data: any, property: string) => {
      switch (property) {
        case 'thingieName':
          return data.name.toLocaleLowerCase();
        case 'assignedDevices':
          return data.thingies[0].assignedDevices[0]?.name;
        case 'dateCreated':
          return data.thingies[0].dateCreated;
        case 'thingieStatus':
          return data.thingies[0].thingieStatus;
        default:
          return data[property];
      }
    };

    this.subscriptions.sink = this.updateTasks$
      .pipe(
        tap(() => {
          this.forceToShowCalculationSpinner.set(true);
        }),
        debounceTime(300),
        tap(() => {
          // refresh the action component with new data
          void this.checkSelectedForRunningTasks();
        })
      )
      .subscribe();

    this.subscriptions.sink = this.dataSortFilter$.pipe(
      debounceTime(DefaultConfig.general.userInputDefaultDebounce_Milliseconds)
    ).subscribe(
      () => {
        this.filterCallbackFunction.emit({
          type: 'RESET',
          extraSortFilter: this.dataSortFilter
        });
      }
    );


    this.subscriptions.sink = SbiResizeObserver.observe(
      this.assignedDevicesHeader()!.nativeElement
    )
      .pipe(debounceTime(60))  // arbitrary value: feel free to improve. What is a good debounce time?
      .subscribe(() => this.updateThingieGroupExtraInfo());


    this.subscriptions.sink = this.editorCheckTrigger$.pipe(
      filter(() => this.isAdmin() === false), // bypass if user is admin
      debounceTime(DefaultConfig.general.userInputDebounce_Milliseconds_100),
      switchMap((thingies) => {
        // list of project ids of the thingies
        let projectIds = thingies.map(t => t.project).filter(isNotUndefined);
        // only the unique project ids
        projectIds = [...new Set(projectIds)];

        this.isEditor.set(false);

        if (projectIds.length > 0) {
          void this.auth.getProjectIdsWithEditorRole()
            .then((projectIdsWithEditorRole) => {
              if (projectIdsWithEditorRole.length === 0) {
                this.isEditor.set(false);
              } else {
                // check if user can edit all the projects (has EDITOR right in all projects)
                this.isEditor.set(projectIds.every(id => projectIdsWithEditorRole.includes(id)));
              }
            });
        }

        return EMPTY;
      })
    ).subscribe();
  }

  // initializing array all columns
  private intializeSearchTermArray() {
    for (let i = 0; i < this.displayedColumns.length; i++) {
      this.searchTerms.push({
        searchString: '',
        searchField: i
      });
    }
  }

  /**
   * watch for value change and filter the results
   */
  private subscribeToFilters() {
    const nameFilter$ = this.thingieNameFilter.valueChanges.pipe(
      debounceTime(this.defaultDebounce_Milliseconds),
      distinctUntilChanged()
    );
    const deviceFilter$ = this.deviceFilter.valueChanges.pipe(
      debounceTime(this.defaultDebounce_Milliseconds),
      distinctUntilChanged()
    );

    const taskFilter$ = this._taskFilter.valueChanges.pipe(
      debounceTime(this.defaultDebounce_Milliseconds),
      distinctUntilChanged()
    );

    this.subscriptions.sink = merge(
      nameFilter$,
      deviceFilter$,
      taskFilter$,
    ).subscribe(
      () => this.triggerFiltering()
    );
  }

  protected datePickerChange(event: MatDatepickerInputEvent<Date>) {
    const date = event.value;
    this.formattedDateFilter = null;
    if (date) {
      this.formattedDateFilter = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    this.dateFilter = event.value;
    this.triggerFiltering();
  }

  /**
   * Update the isPinned state of a card, based on user pins
   *
   * @param card the card to update
   */
  private checkForPin(card: ICard) {
    const pinTimes = card.thingieIds
      .map(id => this.userService.getPinnedThingies().get(id))
      .filter(isNotUndefined);
    if (pinTimes.length > 0) {
      if (!card.isPinned) {
        card.isPinned = true;
      }
    } else {
      card.isPinned = false;
    }
  }

  protected openExperimentGroupDialog(thingieGroup: IGroupedThingies) {
    const cards = cardsFromThingieGroup(thingieGroup, this.thingieService);
    initializeCards(cards, this.tabStorage, this.dataloader, this.generator, this.subscriptions);

    cards.forEach(card => {
      // update pinned card info
      this.checkForPin(card);
      void getTabForCard(card, this.tabStorage).then(tab => {
        if (tab !== undefined) {
          card.previewTab = tab;
          card.popupTab = tab;
        }
      });
    });
    const thingieGroupId = thingieGroup.thingies[0].thingieId;
    const stackedCard = cards.find(card => card.isStack && card.thingieIds[0] === thingieGroupId);

    if (!stackedCard) {
      return;
    }
    const singleCards = cards.filter(card => !card.isStack && stackedCard.thingieIds.includes(card.thingieIds[0]));
    this.dialogData = {
      stackedCard,
      singleCards,
      popupMode: this.defaultPopupMode,
      thingies: this.thingieList,
    };
    this.dialogRef = this.dialogService.open({
      component: StackCardDialogComponent,
      data: this.dialogData,
      sizeMode: DialogSizeMode.Maximize,
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.resizeSubscription !== undefined) {
      this.resizeSubscription.unsubscribe();
    }
  }

  protected async setGroupSelectionState(thingieGroup: IGroupedThingies, newState: boolean) {
    thingieGroup.thingies.forEach(
      thingie => this.thingieCheckedStates.set(thingie.thingieId, newState)
    );

    this.groupCheckedStates.set(
      thingieGroup.name,
      newState ? 'all' : 'none'
    );

    await this.checkSelectedForRunningTasks();
  }

  protected async selectThingie(group: IGroupedThingies, thingie: DisplayThingie, newState: boolean) {
    this.thingieCheckedStates.set(thingie.thingieId, newState);

    // Update group the thingie belongs to

    let all = true;
    let some = false;
    group.thingies.forEach(thingie => {
      const checked = this.getThingieCheckedState(thingie);

      if (checked) {
        some = true;
      } else {
        all = false;
      }
    });

    if (all) {
      this.groupCheckedStates.set(group.name, 'all');
    } else if (some) {
      this.groupCheckedStates.set(group.name, 'some');
    } else {
      this.groupCheckedStates.set(group.name, 'none');
    }

    await this.checkSelectedForRunningTasks();
  }

  protected getAllThingiesCheckedState(): 'all' | 'some' | 'none' {
    if (this.thingieCheckedStates.size === 0) {
      return 'none';
    }

    let some = false;
    let all = true;
    for (const [, checked] of this.thingieCheckedStates) {
      if (checked) {
        some = true;
      } else {
        all = false;
      }
    }

    if (all) {
      return 'all';
    } else if (some) {
      return 'some';
    }

    return 'none';
  }

  protected async selectAllThingies(checked: boolean) {
    for (const [key,] of this.groupCheckedStates) {
      this.groupCheckedStates.set(
        key,
        checked ? 'all' : 'none'
      );
    }

    for (const [key,] of this.thingieCheckedStates) {
      this.thingieCheckedStates.set(key, checked);
    }

    await this.checkSelectedForRunningTasks();
  }

  private async checkIfUserHasEditorRoleForSelectedThingies(thingies: IThingie[]) {
    this.editorCheckTrigger$.next(thingies);
  }

  private async checkSelectedForRunningTasks() {
    // reset the app-task-status-bar component
    this.selectedThingies.set([]);

    this.archivableSelections = false;
    let selected = this.getSelectedDisplayThingies();

    const thingies: IThingie[] = selected.map(t => t.thingie);

    // update the the app-task-status-bar to update the actions
    this.selectedThingies.set(thingies);

    void this.checkIfUserHasEditorRoleForSelectedThingies(thingies);

    this.onlyOneExperimentIsSelected  = false;
    if (thingies.length > 0) {
      // check if all the thingies have same baseName which is unique
      const baseNameToCheck = thingies[0].baseName;
      const allSameBaseName = thingies.every(t => t.baseName === baseNameToCheck);
      if (allSameBaseName) {
        this.onlyOneExperimentIsSelected  = true;
      }
    }

    let archiveCheck = true;
    for (let thingieInfo of selected) {
      const thingie = (await lastValueFrom(this.thingieService.getThingieById(thingieInfo.thingieId).pipe(take(1))))!;
      if (!ThingieStatusHelper.canBeArchived(thingie)) {
        archiveCheck = false;
        break;
      }
    }
    this.archivableSelections = archiveCheck;

    // set the calculationSpinner to false
    this.forceToShowCalculationSpinner.set(false);
  }

  protected downloadThingies() {
    const thingies = this.getSelectedDisplayThingies();

    this.subscriptions.sink = from(thingies).pipe(
      // For each thingie load it's tabs and turn both (thingie and tabs)
      // into the structure required by the export dialog
      mergeMap(
        t => from(
          this.tabStorage.loadCurrentUserTabConfigurations(
            { thingieId: t.thingieId, dashboardCard: false }
          ).then(async result => {
            // Merge the resulting tab list (if any) with the tab of the dashboard card
            const finalList = [...result ?? []];
            const dashboardCard = await TabLoader.getTabForDashboardCard(
              this.tabStorage,
              of(t.thingie),
              t.thingieGroupId,
              t.allReplicateIdsOfExperiment,
              false
            );

            if (dashboardCard) {
              finalList.push(dashboardCard);
            }

            return finalList;
          })
        )
          .pipe(
            switchMap(tabs => {
              if (tabs.length === 0 || (tabs.length === 1 && tabs[0]._isDashboardCard)) {
                // add some default graphs
                return from(extractDefaultGraphConfigsFromThingie(t.thingie))
                  .pipe(
                    map(configList => {
                      const defaultGraphs = [...configList].map<ITab>(g => ({
                        ...createDefaultTab(),
                        graphConfig: {
                          ...g,
                          isDashboardConfig: undefined
                        },
                        _isDashboardCard: g.isDashboardConfig
                      }));
                      if (tabs.length === 1 && tabs[0]._isDashboardCard) {
                        // There's already a dashboard card, so only add non-dashboard default graphs
                        return [...tabs, ...defaultGraphs.filter(g => !g._isDashboardCard)];
                      }
                      return defaultGraphs;
                    })
                  );
              }

              return of(tabs);
            }),
            map(tabs => ({
              graphSelections: !tabs ? [] : tabs.map(t => ({
                graphConfig: {
                  ...t.graphConfig,
                  isDashboardConfig: undefined
                },
                selected: false,
                isDashboardCard: t._isDashboardCard
              })),
              thingie: t.thingie
            }))
          )
      ),
      toArray(),
      map(param => {
        // Group entries by experiment and create arguments
        // to be passed to the export dialog

        const groups = Object.entries(groupBy(param, 'thingie.baseName'));

        const selections: ExportDialogArgs['experiments'] = groups.map(g => ({
          experimentName: g[0],
          replicateSelections: g[1]
        }));

        return selections;
      }),
      switchMap(experiments => this.excelExportService.showExportDialog({
        experiments,
        levelMode: 'experiment',
        canSwitchLevelMode: false,
        title: 'Export of selected replicates'
      })),
      catchError((err: unknown) => {
        this.log.error('Replicate export dialog encountered an error.', err);
        return EMPTY;
      })
    )
      .subscribe();
  }

  protected async restoreThingies() {
    await this.executeArchiveAction('restore');
  }

  protected async archiveThingies() {
    await this.executeArchiveAction('archive');
  }

  private async executeArchiveAction(mode: 'archive' | 'restore') {
    try {
      this.archiveActionInProgress = true;
      const selectedThingieIds = this.getSelectedDisplayThingies().map(t => t.thingieId);

      const promises = selectedThingieIds.map(async thingieId => {
        if (mode === 'archive') {
          await this.thingieService.archiveThingie(thingieId);
        } else {
          await this.thingieService.unarchiveThingie(thingieId);
        }
      });

      await Promise.all(promises);

      let tempMsg = mode === 'archive' ? 'Experiment successfully archived' : 'Experiment successfully unarchived';

      if (promises.length > 1) {
        tempMsg = mode === 'archive' ? `${promises.length} experiments successfully archived` :
          `${promises.length} experiments successfully unarchived`;
      }

      this.uiNotifications.displaySnackbar({
        message: tempMsg
      });
      // Remove the selection states of archived thingies
      // Bug: will crack the filter view for archived thingies.
      // But it should not have an archive button anyways.

      selectedThingieIds.forEach(id => this.thingieCheckedStates.delete(id));

      this.groupCheckedStates.forEach((_, key) => {
        const groupIdx = this._thingieGroups.findIndex(g => g.name === key);
        const group = this._thingieGroups[groupIdx];

        const hasArchived = !!group?.thingies.some(t => selectedThingieIds.includes(t.thingieId));
        if (hasArchived) {
          this.groupCheckedStates.delete(key);
          this._thingieGroups.splice(groupIdx, 1);
        }
      });
      this.tableDataSource.data = this._thingieGroups;
      this.table?.renderRows();
    } finally {
      this.archiveActionInProgress = false;
    }
  }

  protected toggleAllGroupCollapsing() {
    const expandedState = this._thingieGroups.some(thingie => thingie.showGroupDetailToggle === true);

    this._thingieGroups.forEach((tg) => {
      tg.showGroupDetailToggle = !expandedState;
      this._isExpandedMap.set(tg.thingies[0].thingieId, tg.showGroupDetailToggle);
    });

    this.updateCombinedCollapseState();

    this.table?.renderRows();
  }

  private updateCombinedCollapseState() {
    let allOpen = true;
    let someOpen = false;
    this._thingieGroups.forEach(group => {
      if (group.showGroupDetailToggle) {
        someOpen = true;
      } else if (group.showGroupDetailToggle === false) {
        allOpen = false;
      }
    });

    if (allOpen) {
      this.allGroupsCollapseState = 'open';
    } else if (someOpen) {
      this.allGroupsCollapseState = 'indeterminate';
    } else {
      this.allGroupsCollapseState = 'collapsed';
    }
  }

  protected trackByThingieGroupId(_: number, group: IGroupedThingies) {
    return group.thingies[0].thingieGroupId;
  }

  protected trackDisplayThingieById(_: number, thingie: DisplayThingie) {
    return thingie.thingieId;
  }

  /**
   * Filter function set as the filter predicate of the
   * table's data source
   *
   * @param data The thingie group to check
   * @param _ Unused
   */
  private filterPredicate(group: IGroupedThingies, _: string): boolean {
    const thingieNameFilter = (this.thingieNameFilter.value as string).trim().toLowerCase();
    const deviceFilter = (this.deviceFilter.value as string).toLowerCase();

    let matchesThingeNameFilter = group.name.toLocaleLowerCase().includes(thingieNameFilter);
    if (!matchesThingeNameFilter) {
      matchesThingeNameFilter = group.thingies.some(t => ThingieHelper.getReplicateFullname(t.thingie).toLowerCase()
        .includes(thingieNameFilter));
    }

    let matchesDeviceFilter = true;
    if (deviceFilter !== '') {
      const devices = group.thingies.flatMap(t => t.assignedDevices);
      if (devices) {
        matchesDeviceFilter = devices.some(device => device.name.toLowerCase().includes(deviceFilter));
      }
    }

    let matchesDateFilter = true;
    if (this.dateFilter) {
      const dates = group.thingies.flatMap(t => new Date(t.dateCreated));
      matchesDateFilter = dates.some(
        d => d.getDay() === this.dateFilter!.getDay()
          && d.getMonth() === this.dateFilter!.getMonth()
          && d.getFullYear() === this.dateFilter!.getFullYear()
      );
    }

    return matchesThingeNameFilter
        && matchesDeviceFilter
        && matchesDateFilter;
  }

  private triggerFiltering() {
    this.tableDataSource.filter = (++this.filterCounter).toString();

    // Impossible for humans, but possible for bugs
    if (this.filterCounter === Number.MAX_VALUE) {
      this.filterCounter = 0;
    }

    this.dataSortFilter.name.value = this.thingieNameFilter.value ?? '';
    this.dataSortFilter.assigned_devices.value = this.deviceFilter.value ?? '';
    this.dataSortFilter.created.value = this.formattedDateFilter ?? '';

    this.dataSortFilter$.next();
  }

  private getSelectedDisplayThingies(): DisplayThingie[] {
    return Array
      .from(this.thingieCheckedStates.entries())
      .filter(([, checked]) => checked)
      .map(([id,]) => this.findThingieByIdInGroups(id))
      .filter(t => !!t) as DisplayThingie[];
  }

  private findThingieByIdInGroups(id: string): DisplayThingie | undefined {
    let thingie: DisplayThingie | undefined;

    for (let i = 0; i < this._thingieGroups.length && !thingie; ++i) {
      thingie = this
        ._thingieGroups[i]
        .thingies
        .find(t => t.thingieId === id);
    }

    return thingie;
  }

  protected paginationPageSizeCallbackFunction(selectedPageSize: number) {
    this.callbackFunction.emit(selectedPageSize);
  }

  protected projectChangeCallbackFunction(projectFilter: string) {
    this.projectFilter = projectFilter;

    updateDashboardSetting(this.currentUserID, DASHBOARD_PAGE_THINGIES_PROJECT_STORAGE_KEY, projectFilter);

    // reset the thingies
    this.filterCallbackFunction.emit({
      type: 'RESET',
      extraSortFilter: this.dataSortFilter
    });
  }

  /**
   * @private
   * @param thingie
   */
  protected _duplicateReplicate(thingie: IThingie) {
    const queryParams: IWizardQueryParams = {
      mode: 'DUPLICATE',
      duplicateReferenceOnly: true,
      referenceId: thingie._id!
    };

    this.dialogRef?.forceClose('noNavigation');

    void this.router.navigate(
      [DefaultUrls.route.thingieCreate],
      { queryParams }
    );
  }

  /**
   * @private
   * @param thingie
   */
  protected _duplicateExperiment(thingie: IThingie) {
    const queryParams: IWizardQueryParams = {
      mode: 'DUPLICATE',
      duplicateReferenceOnly: false,
      referenceId: thingie._id!
    };

    this.dialogRef?.forceClose('noNavigation');

    void this.router.navigate(
      [DefaultUrls.route.thingieCreate],
      { queryParams }
    );
  }

  /**
   * @private
   * @param thingie
   */
  protected _editExperiment(thingie: IThingie) {
    const queryParams: IWizardQueryParams = {
      mode: 'EDIT',
      navigationSourceTitle: 'Dashboard',
      navigationSourceUrl: this.router.url,
      referenceId: thingie._id!
    };

    this.dialogRef?.forceClose('noNavigation');

    void this.router.navigate(
      [DefaultUrls.route.thingieCreate],
      { queryParams }
    );
  }

  /**
   * @private
   * @param thingies
   */
  protected _addAnnotation() {
    const thingies = this.selectedThingies();

    /**
     * projectId: thingies[0].project
     *
     * We can use the project of the first thingie
     * because all selected thingies are from the same project
     * otherwise adding an annotation to multiple experiments
     * with different projectId is not possible.
     */
    if (thingies) {
      void this.dialogService.open({
        component: AnnotationHandlingDialogComponent,
        data: {
          mode: AnnotationHandlingDialogMode.Create,
          defaultThingie: null,
          siblingThingies: thingies,
          projectId: thingies[0].project,
        }
      });
    }
  }


  protected onSortChange(sortState: Sort) {
    const { active, direction } = sortState;
    if (direction) {
      const _newSorting =  direction.toUpperCase() as 'ASC' | 'DESC' | 'NONE';
      switch (active) {
        case 'thingieName':
          this.dataSortFilter.name.sort = _newSorting;
          break;

        case 'thingieStatus':
          this.dataSortFilter.status.sort = _newSorting;
          break;

        case 'assignedDevices':
          this.dataSortFilter.assigned_devices.sort = _newSorting;
          break;

        case 'dateCreated':
          this.dataSortFilter.created.sort = _newSorting;
          break;

        default:
          break;
      }
    } else {
      this.clearSorts();
    }

    this.dataSortFilter$.next();
  }

  private clearSorts() {
    this.dataSortFilter.name.sort = 'NONE';
    this.dataSortFilter.status.sort = 'NONE';
    this.dataSortFilter.assigned_devices.sort = 'NONE';
    this.dataSortFilter.created.sort = 'NONE';
  }


  protected async showAssignedDevices(dt: DisplayThingie): Promise<void> {
    await this.dialogService.open({
      component: AssignedDevicesDialogComponent,
      data: {
        devices: dt.assignedDevices,
        assignedTo: {
          type: 'thingie',
          id: dt.thingieId,
          name: ThingieHelper.getReplicateFullname(dt.thingie),
        },
        matchTerm: this.deviceFilter.value ?? undefined,
      }
    }).afterClosed();
  }


  private updateThingieGroupExtraInfo() {
    this._thingieGroups.forEach(group => {
      group.extraThingieInfo = this.determineGroupedThingiesExtraInfo(group);
    });
  }

  protected getThingiesByIds = (thingies: DisplayThingie[]): Observable<IThingie[]> => {
    if (thingies.length === 0) {
      return of([]);
    }

    const ids = thingies.map(t => t.thingieId);

    return combineLatest(ids.map(id => this.thingieService.getThingieById(id))).pipe(
      startWith([])
    );
  };
}
